import { ChangeEvent, Fragment, useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import {
  faArrowTrendDown,
  faArrowTrendUp,
  faCircleQuestion,
  faHouse,
  faTriangleExclamation,
} from '@fortawesome/pro-duotone-svg-icons'

import AffordabilityMeter from '../../components/affordability-meter/AffordabilityMeter'
import AppLayout from '../../components/app-layout/AppLayout'
import Icon from '../../components/icon/Icon'
import CollectionPicker from '../../components/collection-picker/CollectionPicker'
import InquiryEmailDrafter from '../../components/inquiry-email-drafter/InquiryEmailDrafter'
import ListingBreakdown from '../../components/listing-breakdown/ListingBreakdown'
import PriceSparkline from '../../components/price-sparkline/PriceSparkline'
import ListingTagChip from '../../components/listing-tag-chip/ListingTagChip'
import MatchScoreBadge from '../../components/match-score-badge/MatchScoreBadge'
import TagSuggestion from '../../components/tag-suggestion/TagSuggestion'
import { computeMatchScore } from '../../components/match-score-badge/helpers'
import { useImportedListings } from '../../_hooks/use-imported-listings'
import { useListingDecisions } from '../../_hooks/use-listing-decisions'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import {
  PageStack,
  BackLink,
  HeroSection,
  HeroImage,
  HeroPlaceholder,
  SiteBadge,
  HeroSummary,
  TitleBlock,
  Title,
  Address,
  PriceRow,
  Price,
  PriceTrendPill,
  MetaRow,
  MetaDot,
  ActionRow,
  PrimaryAction,
  SecondaryAction,
  Section,
  SectionHeader,
  SectionTitle,
  SectionStamp,
  PersonalSection,
  PersonalHeader,
  PersonalLabel,
  NoteEditor,
  NoteSavedHint,
  PriceHeadline,
  Description,
  TimelineList,
  TimelineRow,
  TimelineStamp,
  TimelineLabel,
  ImageGallery,
  GalleryImage,
  NotFoundCard,
  NotFoundEmoji,
  NotFoundTitle,
  NotFoundBody,
  ScrapeWarningBlock,
} from './styled'
import { formatMonthlyPrice } from '../../_utilities/format-price'
import {
  buildMetaParts,
  buildPriceChangeSummary,
  compressAvailabilityHistory,
  detailContainerMotion,
  detailItemMotion,
  formatAvailabilityLabel,
  formatObservedAt,
  formatSourceSiteLabel,
} from './helpers'
import { ImportedListingDetailParams } from './types'

/**
 * Detail surface for a single imported listing. Shows hero image, full
 * description, price-history sparkline + headline change, availability
 * timeline, image gallery, and the scrape-status banner if needed.
 *
 * Click-throughs from notification rows or imported-listing cards land
 * here. If the listing id can't be resolved (deleted, wrong user, stale
 * notification link), renders a friendly not-found state with a path
 * back to /saved-listings.
 *
 * @returns {JSX.Element}
 */
const ImportedListingDetailPage = (): JSX.Element => {
  const { listingId } = useParams<ImportedListingDetailParams>()
  const navigate = useNavigate()
  const { listings, removeListing, refreshListing } = useImportedListings()
  const { notes, setListingNote } = useListingDecisions()
  const { profile } = useQuickPassProfile()
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
  const [isRemoving, setIsRemoving] = useState<boolean>(false)
  const [draftNote, setDraftNote] = useState<string>('')
  const [showSavedHint, setShowSavedHint] = useState<boolean>(false)

  const listing = useMemo(
    () => listings.find(entry => entry.id === listingId) ?? null,
    [listings, listingId],
  )

  const persistedNote = listing === null ? '' : (notes[listing.id] ?? '')

  useEffect(() => {
    setDraftNote(persistedNote)
  }, [persistedNote])

  useEffect(() => {
    if (listing === null) {
      return
    }

    if (draftNote === persistedNote) {
      return
    }

    const handle = setTimeout(() => {
      setListingNote(listing.id, draftNote)
      setShowSavedHint(true)
    }, 600)

    return () => {
      clearTimeout(handle)
    }
  }, [draftNote, persistedNote, listing, setListingNote])

  useEffect(() => {
    if (showSavedHint === false) {
      return
    }

    const handle = setTimeout(() => {
      setShowSavedHint(false)
    }, 2400)

    return () => {
      clearTimeout(handle)
    }
  }, [showSavedHint])

  const handleNoteChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setDraftNote(event.target.value)
  }

  const handleBack = (): void => {
    navigate('/saved-listings')
  }

  const handleRefresh = async (): Promise<void> => {
    if (listing === null) {
      return
    }

    setIsRefreshing(true)
    try {
      await refreshListing(listing.id)
    } catch (error) {
      console.error('Failed to refresh listing', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleRemove = async (): Promise<void> => {
    if (listing === null) {
      return
    }

    setIsRemoving(true)
    try {
      await removeListing(listing.id)
      navigate('/saved-listings')
    } catch (error) {
      console.error('Failed to remove listing', error)
      setIsRemoving(false)
    }
  }

  if (listing === null) {
    return (
      <AppLayout contentMaxWidth="standard">
        <PageStack>
          <BackLink type="button" onClick={handleBack}>
            ← Back to saved listings
          </BackLink>
          <NotFoundCard>
            <NotFoundEmoji aria-hidden="true">
              <Icon icon={faCircleQuestion} label="Question mark" />
            </NotFoundEmoji>
            <NotFoundTitle>
              This listing isn&apos;t in your QuickPass
            </NotFoundTitle>
            <NotFoundBody>
              Either it was removed, or this link came from a different account.
              Head back to your saved listings to pick something else to look
              at.
            </NotFoundBody>
          </NotFoundCard>
        </PageStack>
      </AppLayout>
    )
  }

  const metaParts = buildMetaParts(listing)
  const priceChange = buildPriceChangeSummary(listing)
  const matchBreakdown = computeMatchScore(
    listing,
    profile?.unitPreferences ?? null,
  )
  const compressedAvailability = compressAvailabilityHistory(
    listing.availabilityHistory,
  )
  const heroImage = listing.imageUrls[0] ?? null
  const galleryImages = listing.imageUrls.slice(1)
  const isScrapeIncomplete = listing.scrapeStatus !== 'success'

  return (
    <AppLayout contentMaxWidth="standard">
      <PageStack
        variants={detailContainerMotion}
        initial="hidden"
        animate="visible"
      >
        <BackLink
          variants={detailItemMotion}
          type="button"
          onClick={handleBack}
        >
          ← Back to saved listings
        </BackLink>

        <motion.div variants={detailItemMotion}>
          <HeroSection>
            <HeroImage
              style={
                heroImage !== null
                  ? { backgroundImage: `url(${heroImage})` }
                  : undefined
              }
            >
              {heroImage === null ? (
                <HeroPlaceholder>
                  <Icon icon={faHouse} />
                </HeroPlaceholder>
              ) : null}
              <SiteBadge>{formatSourceSiteLabel(listing.sourceSite)}</SiteBadge>
            </HeroImage>

            <HeroSummary>
              <TitleBlock>
                <Title>{listing.title}</Title>
                {listing.fullAddress !== null && listing.fullAddress !== '' ? (
                  <Address>{listing.fullAddress}</Address>
                ) : null}
              </TitleBlock>

              <PriceRow>
                <Price>
                  {formatMonthlyPrice(
                    listing.priceMonthly,
                    listing.priceCurrency,
                  )}
                </Price>
                {priceChange !== null && priceChange.isFlat === false ? (
                  <PriceTrendPill direction={priceChange.isUp ? 'up' : 'down'}>
                    <Icon
                      icon={
                        priceChange.isUp ? faArrowTrendUp : faArrowTrendDown
                      }
                    />{' '}
                    ${priceChange.changedBy.toLocaleString('en-CA')} (
                    {Math.round(priceChange.changedFraction * 100)}%)
                  </PriceTrendPill>
                ) : null}
                {priceChange !== null && priceChange.isFlat === true ? (
                  <PriceTrendPill direction="flat">Steady</PriceTrendPill>
                ) : null}
              </PriceRow>

              {metaParts.length > 0 ? (
                <MetaRow>
                  {metaParts.map((part, index) => (
                    <Fragment key={`${part}-${index}`}>
                      {index > 0 ? <MetaDot>·</MetaDot> : null}
                      <span>{part}</span>
                    </Fragment>
                  ))}
                </MetaRow>
              ) : null}

              {matchBreakdown.hasPreferences === true ? (
                <MatchScoreBadge
                  score={matchBreakdown.score}
                  size="lg"
                  withLabel={true}
                />
              ) : null}

              <AffordabilityMeter
                monthlyRent={listing.priceMonthly}
                size="md"
                withLabel={true}
              />

              <ActionRow>
                <PrimaryAction
                  href={listing.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit on {formatSourceSiteLabel(listing.sourceSite)} ↗
                </PrimaryAction>
                <InquiryEmailDrafter listing={listing} />
                <SecondaryAction
                  type="button"
                  onClick={handleRefresh}
                  disabled={isRefreshing === true}
                >
                  {isRefreshing === true ? 'Refreshing…' : 'Refresh now'}
                </SecondaryAction>
                <SecondaryAction
                  type="button"
                  tone="danger"
                  onClick={handleRemove}
                  disabled={isRemoving === true}
                >
                  {isRemoving === true ? 'Removing…' : 'Remove'}
                </SecondaryAction>
              </ActionRow>
            </HeroSummary>
          </HeroSection>
        </motion.div>

        {isScrapeIncomplete === true && listing.scrapeNotes !== '' ? (
          <motion.div variants={detailItemMotion}>
            <ScrapeWarningBlock>
              <Icon icon={faTriangleExclamation} /> {listing.scrapeNotes}
            </ScrapeWarningBlock>
          </motion.div>
        ) : null}

        <motion.div variants={detailItemMotion}>
          <TagSuggestion listing={listing} />
        </motion.div>

        <motion.div variants={detailItemMotion}>
          <PersonalSection>
            <PersonalHeader>
              <SectionTitle>Your take</SectionTitle>
              <ListingTagChip listingId={listing.id} />
              <CollectionPicker listingId={listing.id} />
            </PersonalHeader>
            <PersonalLabel>
              Notes only you can see — auto-saves as you type.
            </PersonalLabel>
            <NoteEditor
              value={draftNote}
              onChange={handleNoteChange}
              placeholder="Maybe note that the bedroom faces north, or that the landlord seemed cool, or that the photos look way too staged…"
            />
            {showSavedHint === true ? (
              <NoteSavedHint>Saved.</NoteSavedHint>
            ) : null}
          </PersonalSection>
        </motion.div>

        <motion.div variants={detailItemMotion}>
          <ListingBreakdown listing={listing} />
        </motion.div>

        <motion.div variants={detailItemMotion}>
          <Section>
            <SectionHeader>
              <SectionTitle>Price history</SectionTitle>
              <SectionStamp>
                Last checked {formatObservedAt(listing.lastScrapedAt)}
              </SectionStamp>
            </SectionHeader>
            {priceChange !== null && priceChange.isFlat === false ? (
              <PriceHeadline>
                {priceChange.isUp ? 'Up' : 'Down'} $
                {priceChange.changedBy.toLocaleString('en-CA')} (
                {Math.round(priceChange.changedFraction * 100)}%) since the
                first time we checked.
              </PriceHeadline>
            ) : null}
            <PriceSparkline entries={listing.priceHistory} />
          </Section>
        </motion.div>

        <motion.div variants={detailItemMotion}>
          <Section>
            <SectionTitle>Availability timeline</SectionTitle>
            {compressedAvailability.length === 0 ? (
              <PriceHeadline>
                We haven&apos;t observed any availability changes yet.
              </PriceHeadline>
            ) : (
              <TimelineList>
                {compressedAvailability.map((entry, index) => (
                  <TimelineRow key={`${entry.observedAt}-${index}`}>
                    <TimelineStamp>
                      {formatObservedAt(entry.observedAt)}
                    </TimelineStamp>
                    <TimelineLabel>
                      {formatAvailabilityLabel(entry)}
                    </TimelineLabel>
                  </TimelineRow>
                ))}
              </TimelineList>
            )}
          </Section>
        </motion.div>

        {listing.description !== '' ? (
          <motion.div variants={detailItemMotion}>
            <Section>
              <SectionTitle>About this place</SectionTitle>
              <Description>{listing.description}</Description>
            </Section>
          </motion.div>
        ) : null}

        {galleryImages.length > 0 ? (
          <motion.div variants={detailItemMotion}>
            <Section>
              <SectionTitle>More photos</SectionTitle>
              <ImageGallery>
                {galleryImages.map((url, index) => (
                  <GalleryImage
                    key={`${url}-${index}`}
                    style={{ backgroundImage: `url(${url})` }}
                  />
                ))}
              </ImageGallery>
            </Section>
          </motion.div>
        ) : null}
      </PageStack>
    </AppLayout>
  )
}

export default ImportedListingDetailPage
